import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function IntakeInfoStep({ form, updateForm }) {
    const el = React.createElement;
    const u = (f, v) => updateForm({ [f]: v });
    const [staffList, setStaffList] = useState({ attorneys: [], intake_coordinators: [] });
    
    useEffect(() => {
        async function loadStaff() {
            try {
                const res = await axios.get(API_URL + '/api/users/staff');
                setStaffList(res.data);
            } catch (e) {
                console.error('Failed to load staff list');
            }
        }
        loadStaff();
    }, []);
    
    const isNotRetained = form.status === 'active_not_retained' || form.status === 'inactive';
    
    return el('div', { className: 'space-y-6' },
        // Client Status - First (prominent)
        el('div', { className: 'space-y-2' },
            el(Label, { className: 'text-base font-medium' }, 'Client Status'),
            el('select', { 
                value: form.status || 'active_retained', 
                onChange: e => u('status', e.target.value),
                className: `w-full md:w-64 h-12 px-4 border-2 bg-background text-base rounded-none focus:outline-none focus:ring-2 focus:ring-ring ${
                    form.status === 'active_retained' ? 'border-green-500 bg-green-50' :
                    form.status === 'active_not_retained' ? 'border-amber-500 bg-amber-50' :
                    'border-stone-300 bg-stone-50'
                }`,
                'data-testid': 'client-status-select'
            },
                el('option', { value: 'active_retained' }, 'Active & Retained'),
                el('option', { value: 'active_not_retained' }, 'Active & Not Retained'),
                el('option', { value: 'inactive' }, 'Inactive')
            )
        ),
        
        // Staff Assignment Row
        el('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4' },
            el('div', { className: 'space-y-2' },
                el(Label, null, 'Attorney Assigned'),
                el('select', { 
                    value: form.attorney_assigned || '', 
                    onChange: e => u('attorney_assigned', e.target.value),
                    className: 'w-full h-10 px-3 border border-input bg-background text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-ring',
                    'data-testid': 'attorney-assigned-select'
                },
                    el('option', { value: '' }, 'Select Attorney...'),
                    staffList.attorneys.map(att => 
                        el('option', { key: att.id, value: att.name }, att.name)
                    )
                )
            ),
            el('div', { className: 'space-y-2' },
                el(Label, null, 'Intake Coordinator'),
                el('select', { 
                    value: form.intake_coordinator || '', 
                    onChange: e => u('intake_coordinator', e.target.value),
                    className: 'w-full h-10 px-3 border border-input bg-background text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-ring',
                    'data-testid': 'intake-coordinator-select'
                },
                    el('option', { value: '' }, 'Select Intake Coordinator...'),
                    staffList.intake_coordinators.map(ic => 
                        el('option', { key: ic.id, value: ic.name }, ic.name)
                    )
                )
            )
        ),
        
        // Divider
        el('div', { className: 'border-t border-stone-300' }),
        
        // Client Name Row
        el('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4' },
            el('div', { className: 'space-y-2' },
                el(Label, { className: 'font-medium' }, 'First Name *'),
                el(Input, { 
                    value: form.first_name || '', 
                    onChange: e => u('first_name', e.target.value), 
                    className: 'rounded-none',
                    'data-testid': 'first-name-input'
                })
            ),
            el('div', { className: 'space-y-2' },
                el(Label, null, 'Middle Name'),
                el(Input, { 
                    value: form.middle_name || '', 
                    onChange: e => u('middle_name', e.target.value), 
                    className: 'rounded-none'
                })
            ),
            el('div', { className: 'space-y-2' },
                el(Label, { className: 'font-medium' }, 'Last Name *'),
                el(Input, { 
                    value: form.last_name || '', 
                    onChange: e => u('last_name', e.target.value), 
                    className: 'rounded-none',
                    'data-testid': 'last-name-input'
                })
            )
        ),
        
        // Referral & Consultation Row
        el('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4' },
            el('div', { className: 'space-y-2' },
                el(Label, null, 'Referral Source'),
                el(Input, { 
                    value: form.referral_source || '', 
                    onChange: e => u('referral_source', e.target.value), 
                    className: 'rounded-none',
                    placeholder: 'How did they find us?',
                    'data-testid': 'referral-source-input'
                })
            ),
            el('div', { className: 'space-y-2' },
                el(Label, null, 'Consultation Date'),
                el(Input, { 
                    type: 'date',
                    value: form.consultation_date || '', 
                    onChange: e => u('consultation_date', e.target.value), 
                    className: 'rounded-none',
                    'data-testid': 'consultation-date-input'
                })
            )
        ),
        
        // Fee Amount
        el('div', { className: 'space-y-2' },
            el(Label, null, 'Fee Amount'),
            el(Input, { 
                type: 'text',
                value: form.fee_amount || '', 
                onChange: e => u('fee_amount', e.target.value), 
                className: 'rounded-none w-full md:w-48',
                placeholder: '$0.00',
                'data-testid': 'fee-amount-input'
            })
        ),
        
        // Services Requested - Checkboxes only
        el('div', { className: 'space-y-3' },
            el(Label, { className: 'text-base' }, 'Services Requested'),
            el('div', { className: 'grid grid-cols-2 md:grid-cols-5 gap-4 mt-2' },
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'service-will', 
                        checked: form.services_requested?.will || false, 
                        onCheckedChange: c => u('services_requested', { ...form.services_requested, will: c }),
                        'data-testid': 'service-will-checkbox'
                    }),
                    el(Label, { htmlFor: 'service-will', className: 'text-sm cursor-pointer' }, 'Will')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'service-trust', 
                        checked: form.services_requested?.trust || false, 
                        onCheckedChange: c => u('services_requested', { ...form.services_requested, trust: c }),
                        'data-testid': 'service-trust-checkbox'
                    }),
                    el(Label, { htmlFor: 'service-trust', className: 'text-sm cursor-pointer' }, 'Trust')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'service-poa', 
                        checked: form.services_requested?.poa || false, 
                        onCheckedChange: c => u('services_requested', { ...form.services_requested, poa: c }),
                        'data-testid': 'service-poa-checkbox'
                    }),
                    el(Label, { htmlFor: 'service-poa', className: 'text-sm cursor-pointer' }, 'POA')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'service-amd', 
                        checked: form.services_requested?.amd || false, 
                        onCheckedChange: c => u('services_requested', { ...form.services_requested, amd: c }),
                        'data-testid': 'service-amd-checkbox'
                    }),
                    el(Label, { htmlFor: 'service-amd', className: 'text-sm cursor-pointer' }, 'AMD')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'service-other', 
                        checked: form.services_requested?.other || false, 
                        onCheckedChange: c => u('services_requested', { ...form.services_requested, other: c }),
                        'data-testid': 'service-other-checkbox'
                    }),
                    el(Label, { htmlFor: 'service-other', className: 'text-sm cursor-pointer' }, 'Other')
                )
            )
        ),
        
        // Cost Factors - Checkboxes for pricing considerations
        el('div', { className: 'space-y-3 p-4 border border-amber-200 bg-amber-50/30' },
            el(Label, { className: 'text-base font-medium text-amber-800' }, 'Cost Factors'),
            el('p', { className: 'text-sm text-amber-700 mb-3' }, 'Check all that apply - these may affect estate planning costs'),
            el('div', { className: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' },
                // Family Complexity
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-blended-family', 
                        checked: form.cost_factors?.blended_family || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, blended_family: c }),
                        'data-testid': 'cost-blended-family'
                    }),
                    el(Label, { htmlFor: 'cost-blended-family', className: 'text-sm cursor-pointer' }, 'Blended family')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-minor-children', 
                        checked: form.cost_factors?.minor_children || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, minor_children: c }),
                        'data-testid': 'cost-minor-children'
                    }),
                    el(Label, { htmlFor: 'cost-minor-children', className: 'text-sm cursor-pointer' }, 'Minor children')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-special-needs', 
                        checked: form.cost_factors?.special_needs_beneficiary || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, special_needs_beneficiary: c }),
                        'data-testid': 'cost-special-needs'
                    }),
                    el(Label, { htmlFor: 'cost-special-needs', className: 'text-sm cursor-pointer' }, 'Special needs beneficiary')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-disinheritance', 
                        checked: form.cost_factors?.disinheritance || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, disinheritance: c }),
                        'data-testid': 'cost-disinheritance'
                    }),
                    el(Label, { htmlFor: 'cost-disinheritance', className: 'text-sm cursor-pointer' }, 'Disinheritance planned')
                ),
                
                // Health Concerns
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-dementia', 
                        checked: form.cost_factors?.dementia_alzheimers || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, dementia_alzheimers: c }),
                        'data-testid': 'cost-dementia'
                    }),
                    el(Label, { htmlFor: 'cost-dementia', className: 'text-sm cursor-pointer' }, "Alzheimer's / Dementia diagnosis")
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-terminal', 
                        checked: form.cost_factors?.terminal_illness || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, terminal_illness: c }),
                        'data-testid': 'cost-terminal'
                    }),
                    el(Label, { htmlFor: 'cost-terminal', className: 'text-sm cursor-pointer' }, 'Terminal illness')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-capacity', 
                        checked: form.cost_factors?.capacity_concerns || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, capacity_concerns: c }),
                        'data-testid': 'cost-capacity'
                    }),
                    el(Label, { htmlFor: 'cost-capacity', className: 'text-sm cursor-pointer' }, 'Capacity concerns')
                ),
                
                // Property & Assets
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-significant-assets', 
                        checked: form.cost_factors?.significant_assets || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, significant_assets: c }),
                        'data-testid': 'cost-significant-assets'
                    }),
                    el(Label, { htmlFor: 'cost-significant-assets', className: 'text-sm cursor-pointer' }, 'Significant assets (>$1M)')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-multiple-properties', 
                        checked: form.cost_factors?.multiple_properties || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, multiple_properties: c }),
                        'data-testid': 'cost-multiple-properties'
                    }),
                    el(Label, { htmlFor: 'cost-multiple-properties', className: 'text-sm cursor-pointer' }, 'Multiple properties')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-out-of-state', 
                        checked: form.cost_factors?.out_of_state_property || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, out_of_state_property: c }),
                        'data-testid': 'cost-out-of-state'
                    }),
                    el(Label, { htmlFor: 'cost-out-of-state', className: 'text-sm cursor-pointer' }, 'Property outside Virginia')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-business-owner', 
                        checked: form.cost_factors?.business_owner || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, business_owner: c }),
                        'data-testid': 'cost-business-owner'
                    }),
                    el(Label, { htmlFor: 'cost-business-owner', className: 'text-sm cursor-pointer' }, 'Business owner')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-complex-investments', 
                        checked: form.cost_factors?.complex_investments || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, complex_investments: c }),
                        'data-testid': 'cost-complex-investments'
                    }),
                    el(Label, { htmlFor: 'cost-complex-investments', className: 'text-sm cursor-pointer' }, 'Complex investments')
                ),
                
                // Other Factors
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-existing-trust', 
                        checked: form.cost_factors?.existing_trust || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, existing_trust: c }),
                        'data-testid': 'cost-existing-trust'
                    }),
                    el(Label, { htmlFor: 'cost-existing-trust', className: 'text-sm cursor-pointer' }, 'Has existing trust to amend')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-contested-estate', 
                        checked: form.cost_factors?.contested_estate || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, contested_estate: c }),
                        'data-testid': 'cost-contested-estate'
                    }),
                    el(Label, { htmlFor: 'cost-contested-estate', className: 'text-sm cursor-pointer' }, 'Potential contested estate')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-charitable', 
                        checked: form.cost_factors?.charitable_giving || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, charitable_giving: c }),
                        'data-testid': 'cost-charitable'
                    }),
                    el(Label, { htmlFor: 'cost-charitable', className: 'text-sm cursor-pointer' }, 'Charitable giving planned')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-tax-planning', 
                        checked: form.cost_factors?.tax_planning_needed || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, tax_planning_needed: c }),
                        'data-testid': 'cost-tax-planning'
                    }),
                    el(Label, { htmlFor: 'cost-tax-planning', className: 'text-sm cursor-pointer' }, 'Tax planning needed')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { 
                        id: 'cost-rush', 
                        checked: form.cost_factors?.rush_request || false, 
                        onCheckedChange: c => u('cost_factors', { ...form.cost_factors, rush_request: c }),
                        'data-testid': 'cost-rush'
                    }),
                    el(Label, { htmlFor: 'cost-rush', className: 'text-sm cursor-pointer' }, 'Rush / Expedited request')
                )
            )
        ),
        
        // Intake Notes
        el('div', { className: 'space-y-2' },
            el(Label, null, 'Intake Notes'),
            el(Textarea, { 
                value: form.intake_notes || '', 
                onChange: e => u('intake_notes', e.target.value), 
                className: 'rounded-none min-h-[100px]',
                placeholder: 'Notes from initial call or consultation...',
                'data-testid': 'intake-notes-textarea'
            })
        ),
        
        // Stop Point Warning - Shows when client is NOT retained
        isNotRetained && el('div', { 
            className: 'mt-8 p-4 border-2 border-amber-500 bg-amber-50 flex items-start gap-3',
            'data-testid': 'not-retained-warning'
        },
            el(AlertTriangle, { className: 'h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5' }),
            el('div', null,
                el('p', { className: 'font-medium text-amber-800' }, 'Stop Point'),
                el('p', { className: 'text-sm text-amber-700 mt-1' }, 
                    'Client is not retained. You can save this intake record now, or continue to fill out additional information if needed.'
                )
            )
        )
    );
}
